<script lang="ts">
import { computed, defineComponent, onActivated } from "vue";
import cookieUtils from "@/businesslogic/utils/cookieUtils";
import Logger from "js-logger";
import storeUtils from "@/businesslogic/utils/storeUtils";
import { useFormStore } from "@/businesslogic/stores/formStore";

declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

export default defineComponent({
  name: "Bestaetigung",
  components: {},
  setup(props) {
    const piniaStore = useFormStore();
    const { getCookie } = cookieUtils();
    const { istVhvVerkaufsprodukt } = storeUtils();

    function isDunkelverarbeitung() {
      return piniaStore.state.isDunkelverarbeitung;
    }

    function isAblehnung() {
      return piniaStore.state.status === "ABGELEHNT";
    }

    function isAenderungsantrag() {
      if (piniaStore.state.vorversicherung.antragsart.toLowerCase() === "aenderung") {
        return true;
      }
      if (piniaStore.state.vorversicherung.antragsart.toLowerCase() === "wechsel") {
        return (
          piniaStore.state.vorversicherung.gesellschaft.toLowerCase().includes("gev") ||
          piniaStore.state.vorversicherung.gesellschaft.toLowerCase().includes("grundeigent")
        );
      }
      return false;
    }

    function getNettoJahresbeitrag(): number {
      let vSteuer = 19;
      let bruttobeitrag = piniaStore.state.preise.produktLinien[piniaStore.state.preise.produktLinienIndex].jaehrlich;

      if (piniaStore.state.verkaufsprodukt.toLowerCase() === "vgv") {
        vSteuer = 16.34;
      } else if (istVhvVerkaufsprodukt(piniaStore.state)) {
        vSteuer = 16.15;
      } else if (
        piniaStore.state.verkaufsprodukt.toLowerCase() === "bh" ||
        piniaStore.state.verkaufsprodukt.toLowerCase() === "bl"
      ) {
        bruttobeitrag = piniaStore.state.preise.produktLinien[0].einmalig;
      }

      return Math.round((bruttobeitrag / (100 + vSteuer)) * 100 * 100) / 100;
    }

    function gtmDataLayer() {
      if (piniaStore.state.isMakler) {
        return;
      }

      let event;
      let gev_conv_label = "Kauf";
      let gev_conv_uri = "Conversion";

      if (isAblehnung()) {
        event = "gev_abln_atmt";
        gev_conv_label = "Ablehnung";
        gev_conv_uri = "Rejection";
      } else if (!isDunkelverarbeitung()) {
        event = "gev_conv_asst";
      } else {
        event = "gev_conv_dnkl";
      }
      if (isAenderungsantrag()) {
        gev_conv_label = "Aenderung";
        gev_conv_uri = "gev_aend_antr";
      }

      const dataLayerInhalt = {
        event: event,
        gev_conv_cat: "Abschluss",
        gev_conv_label: gev_conv_label,
        gev_conv_uri: gev_conv_uri,
        gev_conv_value_net: getNettoJahresbeitrag(),
        gev_conv_product: piniaStore.state.verkaufsprodukt,
        gev_conv_id: piniaStore.state.antragId,
        gev_conv_session_id: piniaStore.state.sessionId,
        gev_conv_user_id: getCookie("Vid") ? getCookie("Vid") : getCookie("vid"),
      };
      Logger.debug("GTM DataLayer.push: ", dataLayerInhalt);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(dataLayerInhalt);
    }

    onActivated(() => {
      gtmDataLayer();
    });

    const financeAdsTrackingUrl = computed(() => {
      if (
        piniaStore.state.antragId &&
        !piniaStore.state.isMakler &&
        !isAblehnung() &&
        getCookie("s_id") != null &&
        getCookie("s_id") != undefined &&
        getCookie("s_id") != ""
      ) {
        return encodeURI(
          process.env.VUE_APP_FINANCEADS_URL +
            "?p=626&oid=" +
            piniaStore.state.antragId +
            "&ocategory=" +
            piniaStore.state.verkaufsprodukt +
            "&s_id=" +
            getCookie("s_id"),
        );
      }
      return "";
    });

    return {
      props,
      piniaStore,
      isDunkelverarbeitung,
      isAblehnung,
      financeAdsTrackingUrl,
    };
  },
});
</script>

<template>
  <!-- Makler Schlusstext -->
  <section v-if="piniaStore.state.isMakler">
    <h2 class="u-pad-1 u-mar-0 u-text-align-center">Vielen Dank für Ihren Antrag!</h2>
    <p class="u-copy-l u-pad-t-2">Ihre Daten wurden erfolgreich übermittelt.</p>
    <p class="u-copy-l u-pad-t-2">
      Liebe Vertriebspartnerin, lieber Vertriebspartner,<br /><br />
      wir freuen uns über Ihren Antrag und werden diesen umgehend prüfen. Sie erhalten parallel eine Bestätigung per
      E-Mail. Sollten wir Rückfragen haben, setzen wir uns mit Ihnen in Verbindung.<br /><br />
      Gerne stehen wir Ihnen bei Fragen mit Rat und Tat zur Seite. Sie erreichen uns unter der Telefonnummer 040 3766
      3222.<br /><br />
      Ihr GEV Maklervertrieb
    </p>
  </section>

  <!-- Ablehnung -->
  <section v-else-if="isAblehnung()" class="u-pad-t-3 u-pad-b-4">
    <h2 class="u-pad-1 u-mar-0 u-text-align-center">Es tut uns leid.</h2>
    <p class="u-copy-l u-pad-t-2">
      Vielen Dank für Ihr Vertrauen und Ihren Antrag.
      <strong>Leider können wir Ihnen keinen Versicherungsschutz anbieten.</strong>
    </p>
    <p class="u-copy-l u-pad-t-1">Vielen Dank für Ihr Verständnis.</p>
  </section>

  <!-- Dunkelverarbeitung -->
  <section v-else-if="isDunkelverarbeitung()" class="u-pad-t-2 u-pad-b-1">
    <h2 class="u-mar-0 u-text-align-center">Herzlich willkommen bei der GEV.</h2>
    <p class="u-copy-l u-pad-t-2 u-text-align-center">
      Wir greifen nach den Sternen! Bitte bewerten Sie uns bei
      <strong>Google</strong> oder
      <span style="color: #599f7c">&starf;</span>
      <a
        style="color: #000000; text-decoration: none"
        href="https://de.trustpilot.com/evaluate/gev-versicherung.de"
        target="_self"
        ><strong>Trustpilot</strong></a
      >
    </p>
    <div class="o-grid">
      <div
        class="o-grid__cell o-grid__cell--4-1024 o-grid__cell--offset-2-1024 o-grid__cell--12-768 o-grid__cell--offset-0-768"
      >
        <a
          href="https://www.google.com/search?hl=de-DE&gl=de&q=Grundeigent%C3%BCmer-Versicherung,+Gro%C3%9Fe+B%C3%A4ckerstra%C3%9Fe+7,+20095+Hamburg&ludocid=6081362566304488550#lrd=0x47b18f1c9a4849ad:0x546557089aefdc66,3"
          type="button"
          class="c-button c-button--full-width"
          target="_self"
        >
          GEV jetzt bei Google bewerten
        </a>
      </div>
      <div class="o-grid__cell o-grid__cell--4-1024 o-grid__cell--12-768">
        <a
          href="https://de.trustpilot.com/evaluate/gev-versicherung.de"
          type="button"
          class="c-button c-button--full-width"
          target="_self"
        >
          GEV jetzt bei Trustpilot bewerten
        </a>
      </div>
    </div>
    <div class="u-pad-t-1 u-pad-b-2">
      <p class="u-copy-l u-pad-t-2">
        <strong>Schön, dass Sie sich für uns entschieden haben.</strong>
      </p>
      <p class="u-copy-l u-pad-t-1">
        In wenigen Augenblicken senden wir Ihnen die Antragsunterlagen als PDF-Dokument an die von Ihnen angegebene
        E-Mail-Adresse.<br />Sollten Sie noch Fragen haben, wenden Sie sich bitte an unser Beratungsteam unter
        <strong>040 3766 3366</strong>.
      </p>
      <p class="u-copy-l u-pad-t-1">
        Wir möchten Sie auch in Zukunft über Neuerungen bei uns im Haus informieren. Registrieren Sie sich ganz einfach
        jetzt für unseren kostenfreien <strong>Newsletter</strong>.
      </p>
      <p class="u-copy-l u-pad-t-1">
        <strong>Wir freuen uns auf Sie.</strong>
      </p>
    </div>
    <img v-if="financeAdsTrackingUrl" :src="financeAdsTrackingUrl" width="0" height="0" alt="" />
  </section>

  <!-- Aussteuerung / Manuelle Prüfung -->
  <section v-else class="u-pad-t-2 u-pad-b-1">
    <h2 class="u-mar-0 u-text-align-center">Vielen Dank für Ihren Antrag. Wir melden uns bei Ihnen.</h2>
    <div class="u-pad-t-1 u-pad-b-2">
      <p class="u-copy-l u-pad-t-2">
        <strong>Schön, dass Sie sich für uns entschieden haben.</strong>
      </p>
      <p class="u-copy-l u-pad-t-1">
        In wenigen Augenblicken senden wir Ihnen die Antragsunterlagen als PDF-Dokument an die von Ihnen angegebene
        E-Mail-Adresse.<br />Unser Beratungsteam meldet sich schnellstmöglich bei Ihnen, um noch einige offene Punkte zu
        besprechen.<br />Haben Sie Fragen? Dann erreichen Sie uns unter <strong>040 3766 3366</strong>.
      </p>
      <p class="u-copy-l u-pad-t-1">
        Wir möchten Sie auch in Zukunft über Neuerungen bei uns im Haus informieren. Registrieren Sie sich ganz einfach
        jetzt für unseren kostenfreien <strong>Newsletter</strong>.
      </p>
      <p class="u-copy-l u-pad-t-1">
        <strong>Wir freuen uns auf Sie.</strong>
      </p>
    </div>
    <img v-if="financeAdsTrackingUrl" :src="financeAdsTrackingUrl" width="0" height="0" alt="" />
  </section>
</template>
